import { openDialog } from "../../app-dialog/utils/core/dialogManager.js";
import { showContextMenu } from "../../../../app/utils/contextMenu.js";
import { hasPermission, getUserId, getTeamId } from "../../../../app/auth.js";
import { updateFilterOptions, setSplitFields } from "../utils/filterManager.js";
import { toast } from "../../../layout/app-toast/utils/toastManager.js";

import { loadEntries } from "../tableManager/entryManager.js";
import { loadProjects } from "../tableManager/projectManager.js";
import { loadApplications } from "../tableManager/applicationManager.js";
import { loadServices } from "../tableManager/serviceManager.js";
import { loadUsers } from "../tableManager/userManager.js";
import { loadRolePermissions } from "../tableManager/roleManager.js";
import { loadTimeLogs } from "../tableManager/timeLogManager.js";
import { initTime } from "../../app-time/utils/timeManager.js";

import { timeTableConfig } from "../configs/timeTableConfig.js";
import { entryTableConfig } from "../configs/entryTableConfig.js";
import { projectTableConfig } from "../configs/projectTableConfig.js";
import { applicationTableConfig } from "../configs/applicationTableConfig.js";
import { serviceTableConfig } from "../configs/serviceTableConfig.js";
import { userTableConfig } from "../configs/userTableConfig.js";
import { roleTableConfig } from "../configs/roleTableConfig.js";

import { buildContextMenu } from "./contextMenuBuilder.js";
import { attachDefaultEvents } from "./tableEventBuilder.js";
import { teamTableConfig } from "../configs/teamTableConfig.js";
import { loadTeams } from "../tableManager/teamManager.js";
import { timeLogTableConfig } from "../configs/timeLogTableConfig.js";


export let filterInitialized = false;
export function setFilterInitialized(value) { filterInitialized = value; }

export let isUpdatingFilters = false;
export function setIsUpdatingFilters(value) { isUpdatingFilters = value; }


async function createTableDiv() {
    const content = document.querySelector("#content");

    const tableElement = document.createElement("app-table");
    content.appendChild(tableElement);

    await tableElement.ready;
    return tableElement.shadowRoot.querySelector("#table");
}

export async function buildTable(view) {
    let currentTable = null;
    setSplitFields();

    const tableDiv = await createTableDiv();

    switch (view) {
        case "Time":
            currentTable = new Tabulator(tableDiv, {
                ...timeTableConfig(),
                rowContextMenu: buildContextMenu({ update: "Time.Update", ownUpdate: "Time.Update", delete: "Time.Delete", ownDelete: "Time.Delete" })
            });
            currentTable.tableName = "time";
            attachDefaultEvents(currentTable, { updatePermission: "Time.Update", dialogName: "editTime" });
            currentTable.on("tableBuilt", () => initTime(currentTable));
            break;

        case "Timelogs":
            currentTable = new Tabulator(tableDiv, {
                ...timeLogTableConfig(),
                rowContextMenu: (e, component) => {
                    const rowData = component.getData();
                    const canEdit = (
                        hasPermission("Timelogs.Update.Any")
                        || (rowData.memberId == getUserId() && hasPermission("Timelogs.Update.Own"))
                        || (rowData.teamId == getTeamId() && hasPermission("Timelogs.Update.Team"))
                    );
                    const canDelete = (
                        hasPermission("Timelogs.Delete.Any")
                        || (rowData.memberId == getUserId() && hasPermission("Timelogs.Delete.Own"))
                        || (rowData.teamId == getTeamId() && hasPermission("Timelogs.Delete.Team"))
                    );

                    const items = [];
                    items.push({ label: '<i class="fa-solid fa-file-arrow-down"></i> Exportieren', action: () => openDialog("export")})
                    items.push({ separator: true });

                    if (canEdit) {
                        items.push({ label: '<i class="fa-solid fa-pen-to-square"></i> Bearbeiten', action: () => openDialog("editTimeLog", rowData) });
                    }
                    if (hasPermission("Entries.Create")) {
                        items.push({ label: '<i class="fa-solid fa-plus"></i> Leistungsnachweis hinzufügen', action: () => openDialog("createEntry", { workedHours: rowData.duration, date: rowData.date }) });
                    }
                    if (canDelete) {
                        if (items.length) items.push({ separator: true });
                        items.push({ label: '<i class="fa-solid fa-trash"></i> Löschen', cssClass: 'danger', action: () => openDialog("deleteTimeLog", rowData) });
                    }
                    
                    if (items.length) showContextMenu(e, items);
                    return false;
                }
            });
            currentTable.tableName = "timeLog";
            loadTimeLogs(currentTable);
            attachDefaultEvents(currentTable, {
                updateSummary: true,
                updatePermission: "Timelogs.Update.Any",
                ownUpdate: "Timelogs.Update.Own",
                ownUpdateField: "memberId",
                teamUpdate: "Timelogs.Update.Team",
                dialogName: "editTimeLog",
                columnVisibility: { employeeField: "memberId", teamField: "teamId", sessionKey: "filterTimelogs" },
                onFilterChange: () => loadTimeLogs(currentTable),
                onDateFilterChange: () => loadTimeLogs(currentTable)
            });
            break;

        case "Entries":
            currentTable = new Tabulator(tableDiv, {
                ...entryTableConfig(),
                rowContextMenu: buildContextMenu({ 
                    update: "Entries.Update.Any", 
                    ownUpdate: "Entries.Update.Own", 
                    teamUpdate: "Entries.Update.Team", 
                    delete: "Entries.Delete.Any", 
                    ownDelete: "Entries.Delete.Own", 
                    teamDelete: "Entries.Delete.Team", 
                    create: "Entries.Create",
                    factoring: "Entries.Factoring" 
                })
            });
            currentTable.tableName = "entry";
            loadEntries(currentTable);
            attachDefaultEvents(currentTable, {
                updatePermission: "Entries.Update.Any",
                ownUpdate: "Entries.Update.Own",
                teamUpdate: "Entries.Update.Team",
                updateSummary: true,
                columnVisibility: { employeeField: "userId", teamField: "teamId", sessionKey: "filterEntries" },
                onFilterChange: () => loadEntries(currentTable),
                onDateFilterChange: () => loadEntries(currentTable)
            });
            break;

        case "Projects":
            currentTable = new Tabulator(tableDiv, {
                ...projectTableConfig(),
                rowContextMenu: buildContextMenu({ update: "Projects.Update", delete: "Projects.Delete", create: "Projects.Create" })
            });
            currentTable.tableName = "project";
            attachDefaultEvents(currentTable, { updatePermission: "Projects.Update" });
            loadProjects();
            break;

        case "Applications":
            setSplitFields(['project']);
            currentTable = new Tabulator(tableDiv, {
                ...applicationTableConfig(),
                rowContextMenu: buildContextMenu({ update: "Applications.Update", delete: "Applications.Delete", create: "Applications.Create" }, {}, data => ({ ...data, name: null, description: null }))
            });
            currentTable.tableName = "application";
            attachDefaultEvents(currentTable, { updatePermission: "Applications.Update" });
            loadApplications();
            break;

        case "Services":
            currentTable = new Tabulator(tableDiv, {
                ...serviceTableConfig(),
                rowContextMenu: buildContextMenu({ update: "Services.Update", delete: "Services.Delete", create: "Services.Create" }, {}, data => ({ ...data, name: null, description: null }))
            });
            currentTable.tableName = "service";
            attachDefaultEvents(currentTable, { updatePermission: "Services.Update" });
            loadServices();
            break;

        case "Teams":
            setSplitFields(['permission', 'activity']);
            currentTable = new Tabulator(tableDiv, {
                ...teamTableConfig(),
                rowContextMenu: buildContextMenu({ update: "Teams.Update", delete: "Teams.Delete" })
            });
            currentTable.tableName = "team";
            attachDefaultEvents(currentTable, { updatePermission: "Teams.Update", dialogName: "editTeam" });
            loadTeams();
            break;

        case "Users":
            currentTable = new Tabulator(tableDiv, {
                ...userTableConfig(),
                rowContextMenu: buildContextMenu({ update: "Users.Update", delete: "Users.Delete", create: "Users.Create" })
            });
            currentTable.tableName = "user";
            attachDefaultEvents(currentTable, { updatePermission: "Users.Update", dialogName: "editUser" });
            loadUsers(currentTable);
            break;

        case "Roles":
            setSplitFields(['permission']);
            currentTable = new Tabulator(tableDiv, {
                ...roleTableConfig(),
                rowContextMenu: buildContextMenu({ update: "Roles.Update", delete: "Roles.Delete", create: "Roles.Create" })
            });
            currentTable.tableName = "role";
            attachDefaultEvents(currentTable, { updatePermission: "Roles.Update", dialogName: "editRole" });
            loadRolePermissions(currentTable);
            break;
    }

    tableDiv.getRootNode().getElementById("pagination")?.bind(currentTable);

    return currentTable;
}